import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const services = [
  {
    icon: "ri-movie-2-line",
    title: "最新影视推荐",
    desc: "每日整理最新上线的电影与电视剧，结合口碑与热度，帮你第一时间锁定值得一看的作品。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维度分类筛选",
    desc: "按类型、地区、年份自由组合筛选，支持多种排序方式与分页浏览，快速缩小选择范围。",
  },
  {
    icon: "ri-file-text-line",
    title: "作品详情介绍",
    desc: "提供剧情简介、导演与主演阵容、题材标签等信息，并附海报图集，让你全面了解作品。",
  },
  {
    icon: "ri-links-line",
    title: "关联推荐",
    desc: "基于题材与标签智能关联相似作品，从一部好片出发，发现更多同类佳作。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "hello@jinjucinema.com" },
  { icon: "ri-weibo-line", label: "微博账号", value: "@金桔影院" },
  { icon: "ri-customer-service-2-line", label: "内容合作", value: "media@jinjucinema.com" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 金桔影院",
    description:
      "了解金桔影院的平台定位、服务内容与联系方式。金桔影院致力于为影视爱好者提供最新电影与电视剧的推荐、分类筛选与作品详情介绍。",
    keywords: "金桔影院,关于我们,影视平台介绍,影视推荐网站,联系方式",
    canonicalPath: "/about",
  });

  return (
    <div>
      <section className="relative overflow-hidden bg-background-950">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Stylized%20artistic%20cinema%20hall%20interior%20with%20warm%20orange%20ambient%20lighting%20and%20rows%20of%20empty%20seats%2C%20abstract%20modern%20illustration%2C%20soft%20glow%2C%20elegant%20atmosphere%2C%20no%20text&width=1600&height=700&seq=jg-about-hero&orientation=landscape"
            alt="金桔影院介绍"
            title="金桔影院 - 影视推荐平台"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/75 to-background-950/55"></div>
        </div>
        <div className="relative mx-auto w-full max-w-8xl px-4 py-14 md:px-6 md:py-20">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-200 md:text-sm">
            <Link to="/" className="transition-colors hover:text-primary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="font-semibold text-primary-300">关于我们</span>
          </nav>
          <h1 className="mt-6 font-heading text-3xl font-extrabold text-background-50 md:text-5xl">
            关于金桔影院
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
            一个为影视爱好者打造的推荐与资讯门户，专注最新电影和电视剧的推荐、分类与作品介绍，让每一次观影前的选择都更高效、更从容。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-8xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_340px]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700">
              <i className="ri-flag-2-line"></i>
              平台介绍
            </span>
            <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              把宝贵的时间，留给真正的好作品
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-600">
              <p>
                金桔影院以“推荐”为核心，围绕最新电影与电视剧，整理题材、地区、年份等多维信息，构建清晰的内容体系。我们相信，好的推荐能让观众少走弯路，用更少的时间找到更契合口味的作品。
              </p>
              <p>
                平台提供分类筛选、排序、搜索、作品详情与关联推荐等浏览功能，帮助你从浏览到深入了解，一步步完成观影决策。所有内容以信息展示与推荐为主，力求干净、专注、没有干扰。
              </p>
              <p>
                金桔影院目前是一个演示性质的影视推荐门户，站内影视作品及相关资料均为虚构演示内容，用于展示页面结构与交互效果，不代表真实影视资源。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {services.map((service) => (
                <div
                  key={service.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${service.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                    {service.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-5 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-contacts-book-line text-primary-500"></i>
                联系方式
              </h2>
              <ul className="mt-4 flex flex-col gap-3">
                {contacts.map((contact) => (
                  <li key={contact.label} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-primary-600">
                      <i className={contact.icon}></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-400">{contact.label}</span>
                      <span className="block text-sm font-semibold text-foreground-800">
                        {contact.value}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-book-2-line text-accent-500"></i>
                使用说明
              </h2>
              <ul className="mt-4 flex flex-col gap-2.5 text-sm text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
                  通过顶部导航进入电影或电视剧频道，使用筛选筛选心仪作品。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
                  点击任意卡片进入详情页，查看简介、阵容与剧照图集。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
                  使用搜索框按片名、题材或地区快速定位目标作品。
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-copyright-line text-primary-500"></i>
                版权声明
              </h2>
              <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                本站为演示项目，站内影视作品名称、简介、演职人员及图片均为虚构内容或示意素材，仅用于产品功能展示，不构成任何真实影视资源的提供。本站不提供影片播放、下载、购买或任何形式的版权内容分发服务。
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}